import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core'
import { Subscription } from 'rxjs'
import { CommonModule } from '@angular/common'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { CardModule } from 'primeng/card'
import {
  InterventionsService,
  TablotTour,
  TablotTourStep,
  AffairOptimizationRunResponse,
  AffairOptimizationResult,
  AffairOptimizationSlotData,
  AffairPoolItemWithoutSlots,
  OptimizationTechnicianHome,
  CreatePoolAffairResponse,
  SchedulePoolAffairResponse,
} from '../../../interventions/services/interventions.service'
import {
  TablotMapComponent,
  ScheduledInterventionMarker,
  ScheduledRoute,
  CitySelection,
} from '../tablot-map/tablot-map.component'
import { InterventionCalendarModel } from '../../../../core/models/intervention-calendar.model'
import { UserService } from '../../../users/services/user.service'
import {
  PoolMarker,
  TechnicianHome,
  OptimizationTour,
} from './affair-pool-optimization.component.model'
import {
  PoolRealtimeService,
  PoolChange,
} from '../../services/pool-realtime.service'
import { SectorService } from '../../../sectors/services/sector.service'
import { SectorModel } from '../../../../core/models/sector.model'
import { isPointInSector } from '../../../../core/tools/sector-tools'
import { DayShiftMove, TourStepView } from './affair-pool-optimization.types'
import { PoolTourListComponent } from './components/pool-tour-list/pool-tour-list.component'
import { PoolTourHeaderComponent } from './components/pool-tour-header/pool-tour-header.component'
import { PoolDayTimelineComponent } from './components/pool-day-timeline/pool-day-timeline.component'
import { PoolNoSlotAffairsComponent } from './components/pool-no-slot-affairs/pool-no-slot-affairs.component'
import { PoolScheduledRouteRecapComponent } from './components/pool-scheduled-route-recap/pool-scheduled-route-recap.component'
import {
  PoolNearbyInterventionsComponent,
  NearbyIntervention,
} from './components/pool-nearby-interventions/pool-nearby-interventions.component'
import {
  PoolScheduledSearchComponent,
  ScheduledSearchResult,
} from './components/pool-scheduled-search/pool-scheduled-search.component'
import {
  PoolAffairCreateDialogComponent,
  PoolAffairCreatedEvent,
} from './components/pool-affair-create-dialog/pool-affair-create-dialog.component'
import { PoolAffairAddExistingDialogComponent } from './components/pool-affair-add-existing-dialog/pool-affair-add-existing-dialog.component'
import { PoolAffairScheduleDialogComponent } from './components/pool-affair-schedule-dialog/pool-affair-schedule-dialog.component'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { ConfirmationService, MessageService } from 'primeng/api'
import {
  ScheduledRouteRecap,
  ScheduledRouteStop,
} from './affair-pool-optimization.types'
import {
  formatDate,
  formatDistance,
  formatDuration,
} from './pool-optimization-formatters'

export type { TourStepView } from './affair-pool-optimization.types'

@Component({
  selector: 'app-affair-pool-optimization',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ProgressSpinnerModule,
    TablotMapComponent,
    PoolTourListComponent,
    PoolTourHeaderComponent,
    PoolDayTimelineComponent,
    PoolNoSlotAffairsComponent,
    PoolScheduledRouteRecapComponent,
    PoolNearbyInterventionsComponent,
    PoolScheduledSearchComponent,
    PoolAffairCreateDialogComponent,
    PoolAffairAddExistingDialogComponent,
    PoolAffairScheduleDialogComponent,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './affair-pool-optimization.component.html',
  styleUrl: './affair-pool-optimization.component.scss',
})
export class AffairPoolOptimizationComponent implements OnInit, OnDestroy {
  loading = true
  error: string | null = null
  private subscriptions: Subscription[] = []
  private interventionsService = inject(InterventionsService)
  private confirmationService = inject(ConfirmationService)
  private messageService = inject(MessageService)
  private poolRealtime = inject(PoolRealtimeService)
  private sectorService = inject(SectorService)
  private userService = inject(UserService)
  private realtimeUnsubscribe: (() => void) | null = null

  // Secteurs (filtrage géographique de la programmation)
  sectors: SectorModel[] = []
  selectedSector: SectorModel | null = null
  private static readonly SECTOR_STORAGE_KEY = 'poolSelectedSectorId'
  /** Nombre d'affaires du vivier par secteur (id secteur → compte). */
  sectorCounts: Record<number, number> = {}
  /** Nombre total d'affaires du vivier géolocalisées (chip « Toute la France »). */
  totalPoolCount = 0

  // Interventions déjà programmées : toujours affichées par défaut
  // (le toggle permet de les masquer ponctuellement).
  showScheduledInterventions = true
  scheduledLoading = false
  scheduledInterventions: ScheduledInterventionMarker[] = []
  /** Cache des interventions récupérées pour l'horizon du run courant. */
  private allScheduledInterventions: InterventionCalendarModel[] | null = null
  /** Cache des IDs de tous les utilisateurs (requis par get-for-calendar). */
  private allTechnicianIds: number[] | null = null
  /**
   * id utilisateur Planify indexé par email. On matche par email car les id
   * varient selon l'erpSource (interventions ERP vs users Planify). Le back
   * ajoute le domicile au routage à partir de cet id Planify (User::find).
   */
  private technicianIdByEmail = new Map<string, number>()

  // Itinéraire (tournée du jour) tracé au clic d'une intervention programmée
  scheduledRoute: ScheduledRoute | null = null
  scheduledRouteRecap: ScheduledRouteRecap | null = null
  /** Ordre des points sur la carte : id intervention → n° d'ordre. */
  scheduledRouteOrder: Record<number, number> | null = null
  scheduledRouteLoading = false
  /** Clé "userId_date" de l'itinéraire affiché (toggle au re-clic). */
  private scheduledRouteKey: string | null = null
  /** Cache des itinéraires déjà calculés, par "userId_date". */
  private scheduledRouteCache = new Map<
    string,
    {
      route: ScheduledRoute
      recap: ScheduledRouteRecap
      order: Record<number, number>
    }
  >()

  // Recherche sur les interventions programmées : filtre les losanges violets
  // sur la carte et affiche un panneau de résultats cliquables à droite.
  scheduledSearchQuery = ''
  scheduledSearchResults: ScheduledSearchResult[] = []
  /** Nombre total de correspondances (avant troncature à scheduledSearchMaxResults). */
  scheduledSearchTotalCount = 0
  selectedScheduledSearchId: number | null = null
  /** Nombre max de résultats affichés dans le panneau. */
  private readonly scheduledSearchMaxResults = 50

  // Ville recherchée sur la carte → panneau « Interventions proches »
  // (remplace le panneau « Sans créneau » tant qu'une ville est active).
  searchedCity: CitySelection | null = null
  nearbyInterventions: NearbyIntervention[] = []
  selectedNearbyInterventionId: number | null = null
  /** Rayon de recherche des interventions proches (km). */
  readonly nearbyRadiusKm = 50
  /** Nombre max d'interventions proches affichées. */
  private readonly nearbyMaxResults = 20

  // Realtime status
  realtimeConnected = true

  // Optimization run data
  run: AffairOptimizationRunResponse | null = null
  affairsWithSlots: AffairOptimizationResult[] = []
  poolAffairsWithoutSlots: AffairPoolItemWithoutSlots[] = []
  runTechnicianHomes: OptimizationTechnicianHome[] = []
  launchLoading = false
  launchError: string | null = null

  // Affairs currently being optimized (progressive feedback)
  optimizingAffairIds = new Set<number>()

  // Tours dont le recalcul VROOM est en attente après un retrait d'affaire
  recalculatingTourKeys = new Set<string>()
  /**
   * Snapshot des tournées en recalcul, indispensable pour conserver une carte
   * placeholder dans la liste quand toutes les affaires de cette tournée ont
   * temporairement disparu (ex: snooze d'une affaire seule sur sa tournée).
   */
  private recalculatingTourSnapshots = new Map<string, OptimizationTour>()
  /** Horodatage (performance.now) d'ajout d'une clé dans recalculatingTourKeys. */
  private spinnerStartTimes = new Map<string, number>()
  /** Durée minimale d'affichage du spinner par clé (évite les éclairs). */
  private readonly MIN_SPINNER_DISPLAY_MS = 1500
  /** Timers pendants pour le retrait différé des clés de spinner. */
  private pendingSpinnerClears = new Map<
    string,
    ReturnType<typeof setTimeout>
  >()

  // Search / filter
  searchQuery = ''
  selectedGroupingName = ''
  /** Groupes masqués sur la carte (synchronisés via (hiddenGroupsChange)). */
  hiddenMapGroups = new Set<string>()
  poolSearchQuery = ''
  /** Filtre rapide : n'afficher que les tournées contenant des affaires source EROS. */
  erosOnly = false

  // Selection state (tour-centric)
  tours: OptimizationTour[] = []
  selectedTour: OptimizationTour | null = null
  highlightedAffairId: number | null = null
  geometryLoading = false

  // Map data (cached to avoid change detection re-creation)
  mapTours: TablotTour[] = []
  mapSelectedTour: TablotTour | null = null
  mapPoolMarkers: PoolMarker[] = []
  mapTechnicianHomes: TechnicianHome[] = []
  readonly emptyAffairs: any[] = []
  readonly emptyInterventions: any[] = []

  // --- Getters ---

  get runCompleted(): boolean {
    return this.run?.status === 'completed'
  }

  get canClearSelection(): boolean {
    return (
      !!this.selectedTour ||
      !!this.highlightedAffairId ||
      this.searchQuery.trim().length > 0 ||
      this.selectedGroupingName.trim().length > 0 ||
      this.poolSearchQuery.trim().length > 0 ||
      this.scheduledSearchQuery.trim().length > 0 ||
      this.erosOnly
    )
  }

  get filteredTours(): OptimizationTour[] {
    const q = this.searchQuery.toLowerCase().trim()
    const group = this.selectedGroupingName.trim().toLowerCase()

    return this.tours.filter((tour) => {
      const textMatch =
        !q ||
        tour.technicianName.toLowerCase().includes(q) ||
        tour.date.includes(q) ||
        tour.poolAffairIds.some((id) => {
          const affair = this.affairsWithSlots.find((a) => a.affairId === id)
          if (!affair) return false
          return (
            affair.affairName.toLowerCase().includes(q) ||
            this.getAffairGroupingName(affair).toLowerCase().includes(q) ||
            (affair.postalCode ?? '').toLowerCase().includes(q) ||
            (affair.locality ?? '').toLowerCase().includes(q)
          )
        })

      // Exclure les tournées dont TOUTES les affaires appartiennent à des groupes masqués
      const hasVisibleAffair =
        this.hiddenMapGroups.size === 0 ||
        tour.poolAffairIds.some((id) => {
          const affair = this.affairsWithSlots.find((a) => a.affairId === id)
          if (!affair) return true
          const affairGroup = this.getAffairGroupingName(affair)
          return !affairGroup || !this.hiddenMapGroups.has(affairGroup)
        })

      const groupMatch =
        !group ||
        tour.poolAffairIds.some((id) => {
          const affair = this.affairsWithSlots.find((a) => a.affairId === id)
          return (
            affair && this.getAffairGroupingName(affair).toLowerCase() === group
          )
        })

      const sectorMatch = this.tourHasAffairInSelectedSector(tour)

      const erosMatch =
        !this.erosOnly ||
        tour.poolAffairIds.some((id) => {
          const affair = this.affairsWithSlots.find((a) => a.affairId === id)
          return (affair?.source ?? '').toLowerCase() === 'eros'
        })

      return textMatch && hasVisibleAffair && groupMatch && sectorMatch && erosMatch
    })
  }

  get filteredPoolAffairsWithoutSlots(): AffairPoolItemWithoutSlots[] {
    const q = this.poolSearchQuery.toLowerCase().trim()
    return this.poolAffairsWithoutSlots.filter((a) => {
      const sectorMatch = this.isInSelectedSector(a.latitude, a.longitude)
      if (!sectorMatch) return false
      if (!q) return true
      return (
        a.affairName.toLowerCase().includes(q) ||
        (a.postalCode ?? '').toLowerCase().includes(q) ||
        (a.locality ?? '').toLowerCase().includes(q)
      )
    })
  }

  get groupingNames(): string[] {
    const names = this.affairsWithSlots
      .map((a) => this.getAffairGroupingName(a))
      .filter((name) => name.length > 0 && !this.hiddenMapGroups.has(name))

    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'))
  }

  get highlightedAffair(): AffairOptimizationResult | null {
    if (!this.highlightedAffairId) return null
    return (
      this.affairsWithSlots.find(
        (a) => a.affairId === this.highlightedAffairId
      ) ?? null
    )
  }

  // ── Affaires vivier manuelles (drafts) : création + finalisation EROS ──

  /** Visibilité du dialog de création manuelle d'affaire vivier. */
  createAffairDialogVisible = false

  /** Visibilité du dialog d'ajout d'une affaire existante au vivier. */
  addExistingAffairDialogVisible = false

  /** Contexte du dialog de finalisation (draft + créneaux ordonnés). */
  manualScheduleContext: {
    affair: AffairOptimizationResult
    proposals: AffairOptimizationSlotData[]
  } | null = null

  /**
   * Affaire manuelle fraîchement créée, en attente de focus : dès que ses
   * créneaux sont chargés (reload ou événement temps réel), on sélectionne
   * sa meilleure tournée (zoom carte + timeline + affaire surlignée).
   */
  private pendingFocusAffairId: number | null = null
  private pendingFocusExpiresAt = 0
  private pendingFocusName: string | null = null

  openCreateAffairDialog(): void {
    this.createAffairDialogVisible = true
  }

  openAddExistingAffairDialog(): void {
    this.addExistingAffairDialogVisible = true
  }

  /**
   * Affaire existante ajoutée au vivier : même cycle de vie qu'un draft
   * manuel (notification optimized/deferred/0 créneau + focus différé via
   * pendingFocusAffairId), on réutilise donc la même logique.
   */
  onExistingAffairAdded(response: PoolAffairCreatedEvent): void {
    this.onPoolAffairCreated(response)
  }

  /**
   * Draft créé : recharge silencieusement puis zoome sur l'affaire et affiche
   * ses créneaux dès qu'ils sont disponibles. La planification incrémentale
   * étant le plus souvent synchrone côté back, l'issue est souvent connue dès
   * la réponse : aucun créneau → notification immédiate. En différé, l'issue
   * est résolue à l'arrivée du slots_recalculated correspondant.
   */
  onPoolAffairCreated(response: PoolAffairCreatedEvent): void {
    const name = response.customerLastname || `Affaire #${response.affairId}`

    // Planification synchrone terminée sans aucun créneau → informer tout de suite
    if (response.optimized && (response.slotsCount ?? 0) === 0) {
      this.notifyNoSlotFound(name)
      this.silentReload()
      return
    }

    // Aucun run complété : la planification attendra le prochain calcul global
    if (!response.optimized && !response.deferred) {
      this.messageService.add({
        severity: 'info',
        summary: 'Affaire ajoutée au vivier',
        detail: `« ${name} » sera planifiée au prochain calcul d'optimisation. Retrouvez-la dans « Sans créneau ».`,
        life: 8000,
      })
      this.silentReload()
      return
    }

    // Créneaux trouvés (focus au reload) ou planification différée en job
    // asynchrone (issue résolue à l'arrivée du slots_recalculated du draft —
    // latence possible de ~1 min selon la disponibilité des workers).
    this.pendingFocusAffairId = response.affairId
    this.pendingFocusName = name
    this.pendingFocusExpiresAt = Date.now() + 300_000
    this.silentReload()
  }

  /** Notification : le draft est au vivier mais aucune tournée ne peut l'accueillir. */
  private notifyNoSlotFound(name: string): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Aucun créneau trouvé',
      detail: `« ${name} » a été ajoutée au vivier, mais aucune tournée ne peut l'accueillir pour l'instant. Retrouvez-la dans la liste « Sans créneau ».`,
      life: 10000,
    })
  }

  /**
   * Sélectionne la meilleure tournée de l'affaire en attente de focus si ses
   * créneaux sont désormais chargés (zoom carte via selectedTour + timeline).
   *
   * @param planningResolved true quand la planification du draft vient de se
   *        terminer (slots_recalculated reçu pour cette affaire) : l'absence
   *        de créneaux est alors définitive → notification à l'utilisateur.
   */
  private tryFocusPendingAffair(planningResolved = false): void {
    if (this.pendingFocusAffairId === null) return

    if (Date.now() > this.pendingFocusExpiresAt) {
      this.pendingFocusAffairId = null
      this.pendingFocusName = null
      return
    }

    const affair = this.affairsWithSlots.find(
      (a) => a.affairId === this.pendingFocusAffairId
    )

    if (!affair || affair.slots.length === 0) {
      // Planification terminée sans créneau → informer, sinon attendre encore
      if (planningResolved) {
        this.notifyNoSlotFound(this.pendingFocusName ?? 'Affaire')
        this.pendingFocusAffairId = null
        this.pendingFocusName = null
      }
      return
    }

    // Meilleur créneau = premier après tri (rank / temps de trajet)
    const bestSlot = affair.slots[0]
    this.pendingFocusAffairId = null
    this.pendingFocusName = null
    this.navigateToAlternativeSlot(bestSlot, affair.affairId)
  }

  /**
   * La programmation de cette affaire passe-t-elle par le dialog de
   * finalisation (pool-affair-schedule-dialog) ? C'est le cas des drafts
   * manuels ET des affaires existantes ajoutées au vivier (source
   * 'existing') : dans les deux cas l'erpSource est résolue via le
   * technicien du créneau choisi.
   */
  private schedulesViaPoolDialog(source: string | null | undefined): boolean {
    return source === 'manual' || source === 'existing'
  }

  /** L'affaire sélectionnée (header) se programme-t-elle via le dialog vivier ? */
  get manualScheduleAvailable(): boolean {
    return this.schedulesViaPoolDialog(this.highlightedAffair?.source)
  }

  /** Seuls les drafts manuels sont supprimables depuis le header. */
  get manualDeleteAvailable(): boolean {
    return this.highlightedAffair?.source === 'manual'
  }

  openManualScheduleForHighlighted(): void {
    const affair = this.highlightedAffair
    if (!affair || !this.schedulesViaPoolDialog(affair.source)) return
    this.openManualScheduleDialog(affair)
  }

  onManualScheduleRequested(step: TourStepView): void {
    if (!step.affairId) return
    const affair = this.affairsWithSlots.find(
      (a) => a.affairId === step.affairId
    )
    if (!affair || !this.schedulesViaPoolDialog(affair.source)) return
    this.openManualScheduleDialog(affair)
  }

  private openManualScheduleDialog(affair: AffairOptimizationResult): void {
    const tour = this.selectedTour

    // Créneaux ordonnés : celui de la tournée affichée en premier (même
    // logique que buildErosScheduleUrl), limité à 10 propositions.
    const orderedSlots = [...affair.slots]
      .sort((a, b) => {
        const aMatch =
          tour && a.date === tour.date && a.technicianId === tour.technicianId
            ? 0
            : 1
        const bMatch =
          tour && b.date === tour.date && b.technicianId === tour.technicianId
            ? 0
            : 1
        if (aMatch !== bMatch) return aMatch - bMatch
        return (a.rank ?? 0) - (b.rank ?? 0)
      })
      .slice(0, 10)

    if (orderedSlots.length === 0) return

    this.manualScheduleContext = { affair, proposals: orderedSlots }
  }

  onManualScheduleClosed(): void {
    this.manualScheduleContext = null
  }

  /**
   * Affaire vivier finalisée (intervention créée dans EROS) : le retrait du
   * vivier et le recalcul des tournées arrivent via le temps réel ; on
   * recharge silencieusement pour un retour immédiat, et on actualise la
   * liste des interventions programmées pour y faire apparaître la nouvelle.
   */
  onPoolAffairScheduled(_response: SchedulePoolAffairResponse): void {
    this.silentReload()
    this.refreshScheduledInterventions()
  }

  /**
   * Actualise les interventions programmées (la nouvelle intervention modifie
   * aussi la tournée du technicien : invalidation du cache d'itinéraires) :
   * refetch immédiat si la couche est affichée, sinon invalidation du cache
   * (refetch au prochain affichage).
   */
  private refreshScheduledInterventions(): void {
    this.invalidateScheduledRouteCache()
    if (this.showScheduledInterventions) {
      this.fetchScheduledInterventions()
    } else {
      this.allScheduledInterventions = null
    }
  }

  /** Suppression demandée depuis la timeline (step d'un draft). */
  onManualDeleteRequested(step: TourStepView): void {
    if (!step.affairId) return
    this.confirmAndDeleteManualDraft(step.affairId, step.name)
  }

  /** Suppression demandée depuis le header (draft sélectionné). */
  onManualDeleteRequestedForHighlighted(): void {
    const affair = this.highlightedAffair
    if (!affair || affair.source !== 'manual') return
    this.confirmAndDeleteManualDraft(affair.affairId, affair.affairName)
  }

  /** Suppression demandée depuis la liste « Sans créneau ». */
  onManualDeleteRequestedForNoSlot(affair: AffairPoolItemWithoutSlots): void {
    if (affair.source !== 'manual') return
    this.confirmAndDeleteManualDraft(affair.affairId, affair.affairName)
  }

  private confirmAndDeleteManualDraft(
    affairId: number,
    affairName: string | null
  ): void {
    this.confirmationService.confirm({
      header: 'Supprimer cette affaire manuelle ?',
      message: `« ${affairName || 'Affaire #' + affairId} » sera retirée du vivier. Cette action est définitive.`,
      icon: 'pi pi-trash',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.interventionsService.deletePoolAffair(affairId).subscribe({
          next: () => this.silentReload(),
          error: () => {
            this.error = null // pas d'écran d'erreur global pour une action ponctuelle
            console.error(
              `Échec de suppression de l'affaire manuelle #${affairId}`
            )
          },
        })
      },
    })
  }

  get selectedTourSteps(): TourStepView[] {
    const tour = this.selectedTour
    if (!tour?.tourData?.steps) return []

    const highlightedId = this.highlightedAffairId
    let jobCounter = 0
    return (tour.tourData.steps as any[]).map((s: any) => {
      const jobType = s.job_type ?? 'unknown'
      let type: TourStepView['type'] = 'pool_affair'
      if (s.type === 'start') type = 'start'
      else if (s.type === 'end') type = 'end'
      else if (jobType === 'scheduled_intervention')
        type = 'scheduled_intervention'

      const isJob = type !== 'start' && type !== 'end'
      const tourStepNumber = isJob ? ++jobCounter : null

      const affair =
        s.affair_id != null
          ? this.affairsWithSlots.find((a) => a.affairId === s.affair_id)
          : undefined

      const stepView: TourStepView = {
        type,
        locationType: s.location_type ?? null,
        name:
          s.names ??
          s.name ??
          (type === 'start' ? 'Départ' : type === 'end' ? 'Retour' : 'Étape'),
        arrival: s.arrival ?? null,
        departure: s.departure ?? null,
        plannedStart: s.planned_start ?? null,
        plannedEnd: s.planned_end ?? null,
        serviceMin: s.service_min ?? 0,
        travelMin: s.travel_min ?? 0,
        distanceKm: s.distance_km ?? null,
        waitingMin: s.waiting_min ?? 0,
        location: s.location ?? null,
        postalCode: s.postal_code ?? undefined,
        locality: s.locality ?? undefined,
        affairId: s.affair_id ?? undefined,
        interventionId: s.intervention_id ?? undefined,
        erpSource: s.erp_source ?? undefined,
        startDate: s.start_date ?? undefined,
        endDate: s.end_date ?? undefined,
        isCurrentAffair: s.affair_id === highlightedId,
        tourStepNumber,
        groupName: affair ? this.getAffairGroupingName(affair) || null : null,
        planningDeadline: affair?.planning_deadline ?? null,
        wishedPlanningStartDate: affair?.wishedPlanningStartDate ?? null,
        wishedPlanningEndDate: affair?.wishedPlanningEndDate ?? null,
        tablotFicheUrl: null,
        moved: s.moved === true,
        shiftMinutes: s.shift_minutes ?? 0,
        originalStart: s.original_start ?? null,
        newStart: s.new_start ?? null,
        doNotShift: s.do_not_shift === true,
        movedDay: s.moved_day === true,
        originalDate: s.original_date ?? null,
        newDate: s.new_date ?? null,
        postponablePeriod: s.postponable_period ?? null,
        timeSlotStart: s.time_slot_start ?? null,
        timeSlotEnd: s.time_slot_end ?? null,
        lastCallReasonType: affair?.last_call_reason_type ?? null,
        lastCallReasonComment: affair?.last_call_reason_comment ?? null,
        erosScheduleUrl: null,
        alternativeSlots:
          type === 'pool_affair' && affair
            ? this.getAffairAlternativeSlots(affair)
            : [],
      }
      stepView.tablotFicheUrl = this.getTablotFicheUrlForStep(stepView)
      stepView.erosScheduleUrl = this.getErosScheduleUrlForStep(stepView)
      stepView.manualDraft =
        type === 'pool_affair' && affair?.source === 'manual'
      stepView.poolScheduleDialog =
        type === 'pool_affair' && this.schedulesViaPoolDialog(affair?.source)
      return stepView
    })
  }

  /**
   * La proposition sélectionnée implique-t-elle de déplacer des interventions
   * programmées sur un AUTRE jour (passe globale, postponablePeriod week/month) ?
   */
  get selectedTourRequiresDayShift(): boolean {
    return this.selectedTour?.tourData?.requires_day_shift === true
  }

  /**
   * Déplacements inter-jours impliqués par la proposition sélectionnée
   * (y compris sur d'autres jours que la tournée affichée : ils ne sont pas
   * dans selectedTourSteps et ne seraient sinon jamais visibles).
   */
  get selectedTourDayShiftMoves(): DayShiftMove[] {
    const moves = this.selectedTour?.tourData?.day_shift_moves
    if (!Array.isArray(moves)) return []
    return moves.map((m: any) => ({
      interventionId: m.intervention_id ?? null,
      name: m.name ?? 'Intervention',
      technicianId: m.technician_id ?? null,
      originalStart: m.original_start ?? null,
      newStart: m.new_start ?? null,
      originalDate: m.original_date ?? null,
      newDate: m.new_date ?? null,
      postponablePeriod: m.postponable_period ?? null,
    }))
  }

  get poolSteps(): TourStepView[] {
    return this.selectedTourSteps.filter((s) => s.type === 'pool_affair')
  }

  get scheduledSteps(): TourStepView[] {
    return this.selectedTourSteps.filter(
      (s) => s.type === 'scheduled_intervention'
    )
  }

  get startStep(): TourStepView | null {
    return this.selectedTourSteps.find((s) => s.type === 'start') ?? null
  }

  get endStep(): TourStepView | null {
    return this.selectedTourSteps.find((s) => s.type === 'end') ?? null
  }

  /** Adresse (CP + ville) du domicile du technicien de la tournée sélectionnée. */
  get selectedTechnicianHomeAddress(): string {
    const tour = this.selectedTour
    if (!tour) return ''

    // 1. Depuis startStep (si backend le renvoie)
    const step = this.startStep
    if (step?.postalCode || step?.locality) {
      return [step.postalCode, step.locality].filter(Boolean).join(' ')
    }

    // 2. Depuis mapTechnicianHomes (enrichi dans rebuildMapTour)
    const home = this.mapTechnicianHomes.find((h) => h.id === tour.technicianId)
    if (home?.postalCode || home?.locality) {
      return [home.postalCode, home.locality].filter(Boolean).join(' ')
    }

    return ''
  }

  get canOpenTablotFiche(): boolean {
    return !!this.getTablotFicheUrl()
  }

  isAffairOptimizing(affairId: number): boolean {
    return this.optimizingAffairIds.has(affairId)
  }

  get tablotFicheUrl(): string | null {
    return this.getTablotFicheUrl()
  }

  getTablotFicheUrlForStep(step: TourStepView): string | null {
    if (step.type !== 'pool_affair' || !step.affairId) return null

    const affair = this.affairsWithSlots.find(
      (a) => a.affairId === step.affairId
    )
    if (!affair || affair.source !== 'tablot') return null

    const baseUrl = affair.tablotFicheUrl?.trim()
    if (!baseUrl) return null

    const proposals = this.buildOrderedTablotProposalsForStep(step, affair)
    if (proposals.length === 0) return null

    const encodedProposals = encodeURIComponent(JSON.stringify(proposals))
    return `${baseUrl}?proposals=${encodedProposals}`
  }

  /**
   * URL de la page de programmation EROS pour l'étape (affaires source eros).
   * Équivalent du bouton « Fiche » Tablot : ouvre /affair/:mlRef/schedule-eros
   * dans un nouvel onglet avec les propositions de créneaux encodées, la
   * proposition correspondant à la tournée affichée en premier.
   */
  getErosScheduleUrlForStep(step: TourStepView): string | null {
    if (step.type !== 'pool_affair' || !step.affairId) return null

    const affair = this.affairsWithSlots.find(
      (a) => a.affairId === step.affairId
    )
    if (!affair || affair.source !== 'eros') return null

    return this.buildErosScheduleUrl(affair, this.selectedTour)
  }

  /** URL de programmation EROS de l'affaire sélectionnée (header de tournée). */
  get erosScheduleUrl(): string | null {
    const affair = this.highlightedAffair
    if (!affair || affair.source !== 'eros') return null
    return this.buildErosScheduleUrl(affair, this.selectedTour)
  }

  /**
   * Construit l'URL /affair/:mlRef/schedule-eros?affairId=…&proposals=…
   * Le mlRef est extrait du groupingName (« ML12345678 - Noms »), format
   * garanti par le webhook eros (ml_ref + ' - ' + noms).
   */
  private buildErosScheduleUrl(
    affair: AffairOptimizationResult,
    tour: OptimizationTour | null
  ): string | null {
    const mlRef = this.extractMlRef(
      affair.groupingName ?? affair.grouping_name ?? null
    )
    if (!mlRef) return null

    // Propositions ordonnées : le créneau de la tournée affichée en premier
    const orderedSlots = [...affair.slots].sort((a, b) => {
      const aMatch =
        tour && a.date === tour.date && a.technicianId === tour.technicianId
          ? 0
          : 1
      const bMatch =
        tour && b.date === tour.date && b.technicianId === tour.technicianId
          ? 0
          : 1
      if (aMatch !== bMatch) return aMatch - bMatch
      return (a.rank ?? 0) - (b.rank ?? 0)
    })

    const proposals = orderedSlots.map((slot) => ({
      date: slot.date,
      arrivalTime: slot.arrivalTime,
      technicianId: slot.technicianId,
      technicianName: slot.technicianName,
      technicianEmail: slot.technicianEmail,
      serviceDurationMin: slot.serviceDurationMin,
    }))
    if (proposals.length === 0) return null

    const encodedProposals = encodeURIComponent(JSON.stringify(proposals))
    let url =
      `/affair/${encodeURIComponent(mlRef)}/schedule-eros` +
      `?affairId=${affair.affairId}&proposals=${encodedProposals}`

    // Fenêtre de planification souhaitée : affichée sur la page de création
    if (affair.wishedPlanningStartDate) {
      url += `&wishedStart=${encodeURIComponent(affair.wishedPlanningStartDate)}`
    }
    if (affair.wishedPlanningEndDate) {
      url += `&wishedEnd=${encodeURIComponent(affair.wishedPlanningEndDate)}`
    }

    return url
  }

  /** Extrait le mlRef d'un groupingName au format « ML12345678 - Noms ». */
  private extractMlRef(groupingName: string | null): string | null {
    if (!groupingName) return null
    const candidate = groupingName.split(' - ')[0]?.trim()
    return candidate && /^ML\w+$/i.test(candidate) ? candidate : null
  }

  onTimelineStepClick(step: TourStepView, event: MouseEvent): void {
    const target = event.target as HTMLElement | null
    if (target?.closest('a,button')) return

    if (step.type !== 'pool_affair' || !step.affairId) return

    this.highlightAffair(step.affairId, { scrollIntoView: true })
  }

  /** Gestionnaire de clic depuis le sous-composant pool-day-timeline. */
  onTimelineAffairClick(affairId: number): void {
    this.highlightAffair(affairId, { scrollIntoView: true })
  }

  /** Gestionnaire de sélection depuis le sous-composant pool-no-slot-affairs. */
  onNoSlotAffairSelected(affair: AffairPoolItemWithoutSlots): void {
    this.selectedNoSlotAffairId =
      this.selectedNoSlotAffairId === affair.affairId ? null : affair.affairId
    if (
      this.selectedNoSlotAffairId === affair.affairId &&
      affair.latitude &&
      affair.longitude
    ) {
      this.tablotMap?.panTo(affair.latitude, affair.longitude)
    }
  }

  // --- Lifecycle ---

  ngOnInit(): void {
    this.loadLatestOptimization()
    this.setupRealtimeListeners()
    this.loadSectors()
  }

  // --- Secteurs ---

  private loadSectors(): void {
    this.sectorService.getAll().subscribe({
      next: (sectors) => {
        this.sectors = sectors
        this.recomputeSectorCounts()
        const savedId = Number(
          localStorage.getItem(
            AffairPoolOptimizationComponent.SECTOR_STORAGE_KEY
          )
        )
        if (savedId) {
          this.selectedSector = sectors.find((s) => s.id === savedId) ?? null
          if (this.selectedSector) this.rebuildMapTour()
        }
      },
      // En cas d'échec, on reste sans filtre secteur (comportement actuel).
      error: () => {},
    })
  }

  onSectorChange(sectorId: number | string | null): void {
    const id = sectorId != null && sectorId !== '' ? Number(sectorId) : null
    this.selectedSector = this.sectors.find((s) => s.id === id) ?? null

    if (this.selectedSector) {
      localStorage.setItem(
        AffairPoolOptimizationComponent.SECTOR_STORAGE_KEY,
        String(this.selectedSector.id)
      )
    } else {
      localStorage.removeItem(
        AffairPoolOptimizationComponent.SECTOR_STORAGE_KEY
      )
    }

    // Une tournée sélectionnée hors secteur n'a plus de sens visuellement.
    if (
      this.selectedTour &&
      !this.tourHasAffairInSelectedSector(this.selectedTour)
    ) {
      this.selectedTour = null
      this.highlightedAffairId = null
    }

    // Le filtrage des interventions programmées dépend du secteur.
    this.recomputeScheduledInterventionMarkers()
    this.rebuildMapTour()
  }

  // --- Interventions déjà programmées (contexte secteur) ---

  toggleScheduledInterventions(): void {
    this.showScheduledInterventions = !this.showScheduledInterventions
    if (!this.showScheduledInterventions) {
      this.scheduledInterventions = []
      this.clearScheduledRoute()
      // Masquer les interventions rend la recherche sans objet.
      this.clearScheduledSearch()
      return
    }
    if (this.allScheduledInterventions === null) {
      this.fetchScheduledInterventions()
    } else {
      this.recomputeScheduledInterventionMarkers()
    }
  }

  /**
   * Récupère les interventions programmées sur l'horizon du run courant
   * (dates min/max des tournées), tous techniciens confondus.
   */
  private fetchScheduledInterventions(): void {
    const dates = this.tours
      .map((t) => t.date)
      .filter((d): d is string => !!d)
      .sort()

    if (dates.length === 0) {
      this.allScheduledInterventions = []
      this.scheduledInterventions = []
      return
    }

    const dateFrom = dates[0]
    const dateTo = dates[dates.length - 1]

    this.scheduledLoading = true

    // Table email → id Planify (pour le routage avec domicile, cf. handler).
    this.loadTechnicianIdByEmail()

    // get-for-calendar exige une liste d'utilisateurs : on charge (et met en cache)
    // les IDs de tous les utilisateurs, puis on filtre les interventions par secteur.
    if (this.allTechnicianIds) {
      this.loadScheduledInterventions(this.allTechnicianIds, dateFrom, dateTo)
      return
    }

    const sub = this.userService.getForSelect().subscribe({
      next: (users) => {
        this.allTechnicianIds = (users ?? [])
          .map((u) => u.value)
          .filter((id): id is number => id != null)
        this.loadScheduledInterventions(this.allTechnicianIds, dateFrom, dateTo)
      },
      error: () => {
        this.scheduledLoading = false
        this.allScheduledInterventions = []
      },
    })
    this.subscriptions.push(sub)
  }

  /**
   * Charge (une fois) la table email → id utilisateur Planify depuis
   * users/filters, qui — contrairement à users/select — renvoie l'email et l'id
   * Planify complets. Indispensable pour que le back retrouve le domicile.
   */
  private loadTechnicianIdByEmail(): void {
    if (this.technicianIdByEmail.size > 0) return
    const sub = this.userService.getUsersWithFilters({}).subscribe({
      next: (users) => {
        this.technicianIdByEmail.clear()
        for (const u of users ?? []) {
          if (u.id != null && u.email) {
            this.technicianIdByEmail.set(u.email.trim().toLowerCase(), u.id)
          }
        }
      },
      error: () => {
        // Best-effort : sans la table, l'itinéraire est tracé sans domicile.
      },
    })
    this.subscriptions.push(sub)
  }

  private loadScheduledInterventions(
    users: number[],
    dateFrom: string,
    dateTo: string
  ): void {
    const sub = this.interventionsService
      .getInterventions({ users, dateFrom, dateTo })
      .subscribe({
        next: (list) => {
          this.scheduledLoading = false
          this.allScheduledInterventions = list ?? []
          this.recomputeScheduledInterventionMarkers()
          this.recomputeNearbyInterventions()
          this.recomputeScheduledSearchResults()
        },
        error: () => {
          this.scheduledLoading = false
          this.allScheduledInterventions = []
        },
      })
    this.subscriptions.push(sub)
  }

  /**
   * Construit les marqueurs d'interventions programmées. Tous les points
   * restent visibles quel que soit le secteur actif : ceux hors secteur sont
   * simplement marqués (inSector = false) pour être atténués sur la carte.
   */
  private recomputeScheduledInterventionMarkers(): void {
    // Le contexte (secteur) change : on masque l'itinéraire éventuellement affiché.
    this.clearScheduledRoute()
    if (!this.showScheduledInterventions || !this.allScheduledInterventions) {
      this.scheduledInterventions = []
      return
    }

    const q = this.scheduledSearchQuery.trim().toLowerCase()

    this.scheduledInterventions = this.allScheduledInterventions
      .filter((it) => !q || this.matchesScheduledSearch(it, q))
      .map((it): ScheduledInterventionMarker | null => {
        const data = it.data
        const lat = this.parseCoord(data?.workSiteLatitude)
        const lng = this.parseCoord(data?.workSiteLongitude)
        if (lat == null || lng == null) return null

        const tech = data?.usersTechnicians?.[0]
        return {
          id: data?.id,
          latitude: lat,
          longitude: lng,
          label: it.title ?? null,
          date: it.start ?? null,
          technicianName: tech
            ? `${tech.firstname} ${tech.lastname}`.trim()
            : null,
          technicianId: tech?.id ?? null,
          technicianEmail: tech?.email ?? null,
          inSector: this.isInSelectedSector(lat, lng),
        }
      })
      .filter((m): m is ScheduledInterventionMarker => m !== null)
  }

  private parseCoord(value: string | null | undefined): number | null {
    if (value == null) return null
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
  }

  // --- Recherche sur les interventions programmées ---

  /** Saisie dans le champ de recherche de la barre au-dessus de la carte. */
  onScheduledSearchQueryChange(query: string): void {
    this.scheduledSearchQuery = query
    this.selectedScheduledSearchId = null

    // Rechercher implique d'afficher les interventions programmées : on
    // réactive le toggle si besoin (déclenche le fetch si cache absent).
    if (query.trim().length > 0 && !this.showScheduledInterventions) {
      this.toggleScheduledInterventions()
    }

    this.recomputeScheduledInterventionMarkers()
    this.recomputeScheduledSearchResults()
  }

  /** Effacement de la recherche (bouton × du champ ou du panneau). */
  clearScheduledSearch(): void {
    if (
      this.scheduledSearchQuery === '' &&
      this.scheduledSearchResults.length === 0
    ) {
      return
    }
    this.scheduledSearchQuery = ''
    this.scheduledSearchResults = []
    this.scheduledSearchTotalCount = 0
    this.selectedScheduledSearchId = null
    this.recomputeScheduledInterventionMarkers()
  }

  /** Clic sur un résultat : centre la carte et trace la tournée du jour. */
  onScheduledSearchResultSelected(item: ScheduledSearchResult): void {
    this.selectedScheduledSearchId = item.id
    if (item.latitude != null && item.longitude != null) {
      this.tablotMap?.panTo(item.latitude, item.longitude, 12)
    }
    if (item.id != null) this.onScheduledInterventionClicked(item.id)
  }

  /**
   * True si l'intervention correspond à la recherche (titre, technicien,
   * CP/ville, date ISO ou date formatée).
   */
  private matchesScheduledSearch(
    it: InterventionCalendarModel,
    q: string
  ): boolean {
    const data = it.data
    const tech = data?.usersTechnicians?.[0]
    const haystack = [
      it.title ?? '',
      tech ? `${tech.firstname} ${tech.lastname}` : '',
      data?.workSitePostalCode ?? '',
      data?.workSiteLocality ?? '',
      (it.start ?? '').substring(0, 10),
      this.formatNearbyDate(it.start),
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  }

  /**
   * Reconstruit le panneau de résultats : interventions correspondant à la
   * recherche, triées par date croissante, tronquées à scheduledSearchMaxResults.
   */
  private recomputeScheduledSearchResults(): void {
    const q = this.scheduledSearchQuery.trim().toLowerCase()
    if (!q || !this.allScheduledInterventions) {
      this.scheduledSearchResults = []
      this.scheduledSearchTotalCount = 0
      return
    }

    const items: ScheduledSearchResult[] = []
    for (const it of this.allScheduledInterventions) {
      if (!this.matchesScheduledSearch(it, q)) continue
      const data = it.data
      const tech = data?.usersTechnicians?.[0]
      items.push({
        id: data?.id ?? null,
        title: it.title ?? 'Intervention',
        date: it.start ?? '',
        dateLabel: this.formatNearbyDate(it.start),
        technicianName: tech
          ? `${tech.firstname} ${tech.lastname}`.trim()
          : null,
        location: [data?.workSitePostalCode, data?.workSiteLocality]
          .filter(Boolean)
          .join(' '),
        latitude: this.parseCoord(data?.workSiteLatitude),
        longitude: this.parseCoord(data?.workSiteLongitude),
      })
    }

    items.sort((a, b) => a.date.localeCompare(b.date))
    this.scheduledSearchTotalCount = items.length
    this.scheduledSearchResults = items.slice(0, this.scheduledSearchMaxResults)
  }

  // --- Interventions proches d'une ville recherchée ---

  /** Ville sélectionnée (ou effacée) dans la recherche rapide de la carte. */
  onCitySelected(city: CitySelection | null): void {
    this.searchedCity = city
    this.selectedNearbyInterventionId = null
    this.recomputeNearbyInterventions()
  }

  /** Fermeture du panneau « Interventions proches » (bouton ×). */
  closeNearbyInterventions(): void {
    this.searchedCity = null
    this.nearbyInterventions = []
    this.selectedNearbyInterventionId = null
  }

  /** Clic sur une intervention proche : centre la carte et trace la tournée du jour. */
  onNearbyInterventionSelected(item: NearbyIntervention): void {
    this.selectedNearbyInterventionId = item.id
    this.tablotMap?.panTo(item.latitude, item.longitude, 12)
    if (item.id != null) this.onScheduledInterventionClicked(item.id)
  }

  /**
   * Reconstruit la liste des interventions programmées autour de la ville
   * recherchée (rayon nearbyRadiusKm, triées par distance croissante).
   */
  private recomputeNearbyInterventions(): void {
    const city = this.searchedCity
    if (!city || !this.allScheduledInterventions) {
      this.nearbyInterventions = []
      return
    }

    const items: NearbyIntervention[] = []
    for (const it of this.allScheduledInterventions) {
      const data = it.data
      const lat = this.parseCoord(data?.workSiteLatitude)
      const lng = this.parseCoord(data?.workSiteLongitude)
      if (lat == null || lng == null) continue

      const distanceKm = this.haversineKm(
        city.latitude,
        city.longitude,
        lat,
        lng
      )
      if (distanceKm > this.nearbyRadiusKm) continue

      const tech = data?.usersTechnicians?.[0]
      items.push({
        id: data?.id ?? null,
        title: it.title ?? 'Intervention',
        dateLabel: this.formatNearbyDate(it.start),
        technicianName: tech
          ? `${tech.firstname} ${tech.lastname}`.trim()
          : null,
        location: [data?.workSitePostalCode, data?.workSiteLocality]
          .filter(Boolean)
          .join(' '),
        distanceKm,
        distanceLabel: this.formatDistanceKm(distanceKm),
        latitude: lat,
        longitude: lng,
      })
    }

    items.sort((a, b) => a.distanceKm - b.distanceKm)
    this.nearbyInterventions = items.slice(0, this.nearbyMaxResults)
  }

  /** "mar. 8 juil. à 14:00" (heure omise si minuit pile). */
  private formatNearbyDate(value?: string | null): string {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    const datePart = d.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    if (d.getHours() === 0 && d.getMinutes() === 0) return datePart
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${datePart} à ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  /** "3,2 km" en dessous de 10 km, "27 km" au-delà. */
  private formatDistanceKm(km: number): string {
    return km < 10
      ? `${km.toFixed(1).replace('.', ',')} km`
      : `${Math.round(km)} km`
  }

  private haversineKm(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  /** Nombre d'affaires du vivier rattachées à un secteur (pour l'affichage en chip). */
  getSectorCount(sector: SectorModel): number {
    return this.sectorCounts[sector.id] ?? 0
  }

  /**
   * Recalcule le nombre d'affaires du vivier (avec et sans créneau) par secteur.
   * Une affaire est comptée dans chaque secteur géographique qui la contient.
   */
  private recomputeSectorCounts(): void {
    const points: Array<{ lat: number; lng: number }> = []
    for (const a of this.affairsWithSlots) {
      if (a.latitude != null && a.longitude != null) {
        points.push({ lat: a.latitude, lng: a.longitude })
      }
    }
    for (const a of this.poolAffairsWithoutSlots) {
      if (a.latitude != null && a.longitude != null) {
        points.push({ lat: a.latitude, lng: a.longitude })
      }
    }

    this.totalPoolCount = points.length

    const counts: Record<number, number> = {}
    for (const sector of this.sectors) {
      let count = 0
      for (const p of points) {
        if (isPointInSector(p.lat, p.lng, sector.geometry)) count++
      }
      counts[sector.id] = count
    }
    this.sectorCounts = counts
  }

  /** True si le point est dans le secteur actif (ou si aucun secteur sélectionné). */
  private isInSelectedSector(
    latitude: number | null | undefined,
    longitude: number | null | undefined
  ): boolean {
    if (!this.selectedSector) return true
    if (latitude == null || longitude == null) return false
    return isPointInSector(latitude, longitude, this.selectedSector.geometry)
  }

  /** True si la tournée contient au moins une affaire dans le secteur actif. */
  private tourHasAffairInSelectedSector(tour: OptimizationTour): boolean {
    if (!this.selectedSector) return true
    return tour.poolAffairIds.some((id) => {
      const affair = this.affairsWithSlots.find((a) => a.affairId === id)
      return (
        !!affair && this.isInSelectedSector(affair.latitude, affair.longitude)
      )
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe())
    this.realtimeUnsubscribe?.()
    // Annuler les retraits diff\u00e9r\u00e9s de spinner
    this.pendingSpinnerClears.forEach((t) => clearTimeout(t))
    this.pendingSpinnerClears.clear()
  }

  // --- Realtime listeners ---

  private setupRealtimeListeners(): void {
    // S'abonner au polling temps réel
    this.realtimeUnsubscribe = this.poolRealtime.subscribe()

    // Affaire retirée → patch local immédiat
    this.subscriptions.push(
      this.poolRealtime.affairRemoved$.subscribe((change) => {
        this.handleAffairRemoved(change)
      })
    )

    // Affaire ajoutée ou réactivée → full reload
    this.subscriptions.push(
      this.poolRealtime.affairAdded$.subscribe(() => {
        this.silentReload()
      })
    )
    this.subscriptions.push(
      this.poolRealtime.affairReactivated$.subscribe(() => {
        this.silentReload()
      })
    )

    // Affaire mise à jour (ex: changement d'adresse EROS) → full reload silencieux
    // pour rafraîchir le point sur la carte ; les nouveaux créneaux arrivent ensuite
    // via slots_recalculated (delta patching).
    this.subscriptions.push(
      this.poolRealtime.affairUpdated$.subscribe(() => {
        this.silentReload()
      })
    )

    // Affaire en cours d'optimisation → feedback progressif
    this.subscriptions.push(
      this.poolRealtime.affairOptimizing$.subscribe((change) => {
        this.handleAffairOptimizing(change)
      })
    )

    // Slots recalculés → delta patching si données disponibles, sinon full reload
    this.subscriptions.push(
      this.poolRealtime.slotsRecalculated$.subscribe((change) => {
        this.handleSlotsRecalculated(change)
        // Draft manuel en attente : si l'événement concerne cette affaire, sa
        // planification est terminée → focus si créneaux, notification sinon.
        const resolvesPendingAffair =
          this.pendingFocusAffairId !== null &&
          (change.affairId === this.pendingFocusAffairId ||
            change.metadata?.['affair_id'] === this.pendingFocusAffairId)
        this.tryFocusPendingAffair(resolvesPendingAffair)
      })
    )

    // Recalcul de tournées démarré (snooze / unsnooze / removal) → afficher un loader
    // sur les paires (date, technicien) qui vont être re-solvées.
    this.subscriptions.push(
      this.poolRealtime.toursRecalculating$.subscribe((change) => {
        this.handleToursRecalculating(change)
      })
    )

    // Full reload demandé (sécurité périodique, optimisation complète sans delta)
    this.subscriptions.push(
      this.poolRealtime.requestFullReload$.subscribe(() => {
        this.silentReload()
      })
    )

    // Statut de connexion
    this.subscriptions.push(
      this.poolRealtime.connected$.subscribe((connected) => {
        this.realtimeConnected = connected
      })
    )
  }

  /**
   * Retire immédiatement une affaire de la liste locale (sans appel serveur).
   * La tournée sélectionnée (date + technicien) reste stable automatiquement.
   */
  private handleAffairRemoved(change: PoolChange): void {
    if (!change.affairId) return

    const removedAffairId = change.affairId

    // Marquer les tournées affectées comme "en cours de recalcul" (avec snapshot pour
    // afficher un placeholder si la tournée devient vide après le retrait).
    const removedAffair = this.affairsWithSlots.find(
      (a) => a.affairId === removedAffairId
    )
    if (removedAffair) {
      const keys: string[] = []
      removedAffair.slots.forEach((slot) => {
        if (slot.technicianId) {
          keys.push(`${slot.date}:${slot.technicianId}`)
        }
      })
      this.markToursRecalculating(keys)
    }

    // Retirer de poolAffairsWithoutSlots si présent
    this.poolAffairsWithoutSlots = this.poolAffairsWithoutSlots.filter(
      (a) => a.affairId !== removedAffairId
    )

    const index = this.affairsWithSlots.findIndex(
      (a) => a.affairId === removedAffairId
    )
    if (index === -1) {
      this.rebuildMapTour()
      return
    }

    // Retirer de la liste
    this.affairsWithSlots = this.affairsWithSlots.filter(
      (a) => a.affairId !== removedAffairId
    )

    // Mettre à jour les tours des affaires restantes
    this.affairsWithSlots = this.affairsWithSlots.map((a) => ({
      ...a,
      slots: a.slots.map((slot) => {
        if (!slot.tourData?.steps) return slot
        const hasRemoved = (slot.tourData.steps as any[]).some(
          (s: any) => s.affair_id === removedAffairId
        )
        if (!hasRemoved) return slot
        return {
          ...slot,
          id: 0,
          geometry: null,
          tourData: slot.tourData
            ? {
                ...slot.tourData,
                steps: (slot.tourData.steps as any[]).filter(
                  (s: any) => s.affair_id !== removedAffairId
                ),
              }
            : slot.tourData,
        }
      }),
    }))

    // Recalculer les rangs
    this.affairsWithSlots = this.affairsWithSlots.map((a, i) => ({
      ...a,
      efficiencyRank: i + 1,
    }))

    if (this.run) {
      this.run = {
        ...this.run,
        affairsWithSlotsCount: this.affairsWithSlots.length,
      }
    }

    // Reconstruire les tournées — selectedTour survit par tourKey
    const prevTourKey = this.selectedTour?.tourKey ?? null
    this.tours = this.buildToursFromAffairs()
    if (prevTourKey) {
      this.selectedTour =
        this.tours.find((t) => t.tourKey === prevTourKey) ?? null
    }
    if (this.highlightedAffairId === removedAffairId) {
      this.highlightedAffairId = null
    }

    this.rebuildMapTour()
  }

  /**
   * Marque une affaire comme "en cours d'optimisation" pour feedback progressif.
   */
  private handleAffairOptimizing(change: PoolChange): void {
    if (!change.affairId) return

    this.optimizingAffairIds = new Set(this.optimizingAffairIds)
    this.optimizingAffairIds.add(change.affairId)
  }

  /**
   * Affiche un loader sur les tournées (paires date×technicien) en cours de recalcul.
   * Émis par le backend avant un re-solve VROOM (snooze, unsnooze, removal).
   * Le spinner est éteint à la réception du `slots_recalculated` correspondant.
   */
  private handleToursRecalculating(change: PoolChange): void {
    const pairs = (change.metadata?.['pairs'] ?? []) as Array<{
      date: string
      technician_id: number
    }>

    if (pairs.length === 0) return

    const keys = pairs.map((p) => `${p.date}:${p.technician_id}`)
    this.markToursRecalculating(keys)
    // Reconstruit la liste des tournées pour faire apparaître d'éventuels placeholders.
    this.rebuildToursPreservingSelection()
  }

  /**
   * Marque une liste de clés de tournées comme en cours de recalcul et mémorise
   * leur snapshot (pour pouvoir afficher un placeholder si la tournée disparaît).
   */
  private markToursRecalculating(keys: string[]): void {
    if (keys.length === 0) return
    const newKeys = new Set(this.recalculatingTourKeys)
    const now = performance.now()
    for (const key of keys) {
      newKeys.add(key)
      this.spinnerStartTimes.set(key, now)
      // Annule un éventuel retrait différé en cours pour cette clé.
      const pending = this.pendingSpinnerClears.get(key)
      if (pending) {
        clearTimeout(pending)
        this.pendingSpinnerClears.delete(key)
      }
      // Mémorise un snapshot si une tournée existe encore pour cette clé.
      if (!this.recalculatingTourSnapshots.has(key)) {
        const tour = this.tours.find((t) => t.tourKey === key)
        if (tour) {
          this.recalculatingTourSnapshots.set(key, { ...tour })
        } else {
          // Pas de tournée actuelle : on construit un placeholder minimal.
          const [date, techIdStr] = key.split(':')
          this.recalculatingTourSnapshots.set(key, {
            tourKey: key,
            date,
            technicianId: Number(techIdStr),
            technicianName: '',
            totalDurationMin: null,
            totalDistanceKm: null,
            stopsCount: null,
            poolAffairIds: [],
            tourData: null,
            geometry: null,
            slotId: 0,
          } as OptimizationTour)
        }
      }
    }
    this.recalculatingTourKeys = newKeys
  }

  /**
   * Demande l'extinction du spinner pour les clés indiquées, en respectant
   * la durée minimale d'affichage (anti-flash). Si plusieurs événements arrivent
   * dans le même tick, on garantit que le spinner reste visible ≥ MIN_SPINNER_DISPLAY_MS.
   *
   * @param keys liste de clés `${date}:${technicianId}` à éteindre, ou null pour tout
   */
  private requestClearRecalculatingKeys(keys: string[] | null): void {
    const now = performance.now()
    const targets = keys ?? Array.from(this.recalculatingTourKeys)
    if (targets.length === 0) return

    const immediate: string[] = []
    const delayed: { key: string; ms: number }[] = []

    for (const key of targets) {
      if (!this.recalculatingTourKeys.has(key)) continue
      const start = this.spinnerStartTimes.get(key)
      const elapsed = start != null ? now - start : Infinity
      const remaining = this.MIN_SPINNER_DISPLAY_MS - elapsed
      if (remaining > 0) {
        delayed.push({ key, ms: remaining })
      } else {
        immediate.push(key)
      }
    }

    if (immediate.length > 0) {
      this.applySpinnerRemoval(immediate)
    }

    for (const { key, ms } of delayed) {
      // Évite les doublons
      const existing = this.pendingSpinnerClears.get(key)
      if (existing) clearTimeout(existing)
      const timer = setTimeout(() => {
        this.pendingSpinnerClears.delete(key)
        this.applySpinnerRemoval([key])
        this.rebuildToursPreservingSelection()
      }, ms)
      this.pendingSpinnerClears.set(key, timer)
    }
  }

  private applySpinnerRemoval(keys: string[]): void {
    const newKeys = new Set(this.recalculatingTourKeys)
    for (const key of keys) {
      newKeys.delete(key)
      this.spinnerStartTimes.delete(key)
      this.recalculatingTourSnapshots.delete(key)
    }
    this.recalculatingTourKeys = newKeys
  }

  /**
   * Reconstruit `this.tours` (incluant les placeholders de recalcul) en conservant
   * la sélection courante. Utilisé quand l'état de recalcul change sans modification
   * de `affairsWithSlots`.
   */
  private rebuildToursPreservingSelection(): void {
    const prevTourKey = this.selectedTour?.tourKey ?? null
    this.tours = this.buildToursFromAffairs()
    if (prevTourKey) {
      this.selectedTour =
        this.tours.find((t) => t.tourKey === prevTourKey) ?? this.selectedTour
    }
  }

  /**
   * Applique les slots recalculés en patch local (delta) si les données sont présentes.
   * Sinon, déclenche un full reload.
   */
  private handleSlotsRecalculated(change: PoolChange): void {
    const affectedSlots = change.affectedSlots
    if (!affectedSlots || Object.keys(affectedSlots).length === 0) {
      this.silentReload()
      return
    }

    // Le recalcul VROOM est terminé. On déterminera plus bas QUELLES paires effacer,
    // mais on attend d'abord d'avoir patché les données + rechargé la géométrie
    // de la tournée sélectionnée avant d'éteindre le spinner. Sinon le bandeau
    // « Recalcul en cours… » disparaît avant que la tournée à jour ne soit visible
    // (effet déstabilisant : loader off → tournée encore vide → tournée affichée).
    const recalculatedPairs = (change.metadata?.['recalculated_pairs'] ??
      []) as Array<{ date: string; technician_id: number }>

    if (change.affairId) {
      this.optimizingAffairIds = new Set(this.optimizingAffairIds)
      this.optimizingAffairIds.delete(change.affairId)
    }

    // Bug 1 : si une affaire recalculée est inconnue (absente de affairsWithSlots,
    // par ex. ajout pendant un silentReload en cours), on recharge tout proprement.
    const hasNewAffair = Object.keys(affectedSlots).some(
      (idStr) =>
        !this.affairsWithSlots.some((a) => a.affairId === Number(idStr))
    )
    if (hasNewAffair) {
      this.silentReload()
      return
    }

    // Patcher les slots des affaires affectées
    // Collecter aussi les tourKeys affectés pour invalider la géométrie (Bug 2)
    const affectedTourKeys = new Set<string>()

    for (const [affairIdStr, slots] of Object.entries(affectedSlots)) {
      const affairId = Number(affairIdStr)
      const newSlots: AffairOptimizationSlotData[] = (slots as any[]).map(
        (s: any) => ({
          id: s.id,
          rank: s.slot_rank ?? 1,
          date:
            typeof s.slot_date === 'string'
              ? s.slot_date.substring(0, 10)
              : s.slot_date,
          arrivalTime: s.arrival_time,
          technicianId: s.technician_id,
          technicianName: s.technician_name,
          technicianEmail: s.technician_email ?? null,
          travelDurationMin: s.travel_duration_min ?? 0,
          serviceDurationMin: s.service_duration_min ?? 0,
          waitingMin: s.waiting_min ?? 0,
          distanceKm: s.distance_km,
          tourTotalDurationMin: s.tour_total_duration_min,
          tourTotalDistanceKm: s.tour_total_distance_km,
          tourStopsCount: s.tour_stops_count,
          geometry: null,
          tourData: s.tour_data,
        })
      )

      // Collecter les tourKeys pour invalider la géométrie
      newSlots.forEach((slot) => {
        if (slot.technicianId) {
          affectedTourKeys.add(`${slot.date}:${slot.technicianId}`)
        }
      })

      const existingIdx = this.affairsWithSlots.findIndex(
        (a) => a.affairId === affairId
      )

      if (existingIdx !== -1) {
        this.affairsWithSlots = this.affairsWithSlots.map((a) =>
          a.affairId === affairId ? { ...a, slots: newSlots } : a
        )
      }
    }

    // Re-trier, retirer les affaires sans slots restants, et reconstruire les tournées
    this.affairsWithSlots = this.sortSlotsByTravel(
      this.affairsWithSlots.filter((a) => a.slots.length > 0)
    )
    const prevTourKey = this.selectedTour?.tourKey ?? null
    this.tours = this.buildToursFromAffairs()

    // Bug 2 : l'affaire 1 (existante) peut conserver son ancienne geometry dans son slot,
    // ce qui ferait que buildToursFromAffairs utilise l'ancienne geometry et que
    // lazyLoadGeometryIfNeeded ne recharge jamais. On invalide explicitement la geometry
    // des tournées affectées pour forcer un rechargement.
    if (affectedTourKeys.size > 0) {
      this.tours = this.tours.map((tour) =>
        affectedTourKeys.has(tour.tourKey) ? { ...tour, geometry: null } : tour
      )
    }

    if (prevTourKey) {
      this.selectedTour =
        this.tours.find((t) => t.tourKey === prevTourKey) ?? null
    }

    this.rebuildMapTour()

    // Le spinner ne sera retiré qu'une fois la géométrie de la tournée sélectionnée
    // chargée (ou immédiatement si aucun chargement de géométrie n'est nécessaire).
    // Garantit que le bandeau « Recalcul en cours… » reste visible jusqu'à ce que
    // la tournée mise à jour soit entièrement affichée (timeline + polyline).
    const clearSpinners = () => {
      if (recalculatedPairs.length > 0) {
        const keys = recalculatedPairs.map(
          (p) => `${p.date}:${p.technician_id}`
        )
        this.requestClearRecalculatingKeys(keys)
      } else {
        // Fallback (anciens événements sans recalculated_pairs) : tout effacer.
        this.requestClearRecalculatingKeys(null)
      }
    }

    this.lazyLoadGeometryIfNeeded(clearSpinners)
  }

  /**
   * Recharge les données sans afficher de loader (mise à jour silencieuse).
   */
  private silentReload(): void {
    if (this.launchLoading) return

    const sub = this.interventionsService
      .getLatestAffairOptimization()
      .subscribe({
        next: (run) => {
          this.run = run
          this.affairsWithSlots =
            run.status === 'completed' && run.affairs?.length
              ? this.sortSlotsByTravel(
                  run.affairs.filter((a) => a.slots.length > 0)
                )
              : []
          this.poolAffairsWithoutSlots = run.poolAffairsWithoutSlots ?? []
          this.optimizingAffairIds = new Set()
          this.requestClearRecalculatingKeys(null)

          // Reconstruire les tournées — selectedTour survit par tourKey
          const prevTourKey = this.selectedTour?.tourKey ?? null
          this.tours = this.buildToursFromAffairs()
          if (prevTourKey) {
            this.selectedTour =
              this.tours.find((t) => t.tourKey === prevTourKey) ?? null
          }

          this.rebuildMapTour()
          this.lazyLoadGeometryIfNeeded()
          this.poolRealtime.resetCursor()

          // Focus sur une affaire manuelle fraîchement créée (zoom + créneaux)
          this.tryFocusPendingAffair()
        },
        error: () => {
          // Silencieux — on réessaiera au prochain poll
        },
      })
    this.subscriptions.push(sub)
  }

  // --- Data loading ---

  loadLatestOptimization(): void {
    this.loading = true
    this.error = null
    const sub = this.interventionsService
      .getLatestAffairOptimization()
      .subscribe({
        next: (run) => {
          this.run = run
          this.affairsWithSlots =
            run.status === 'completed' && run.affairs?.length
              ? this.sortSlotsByTravel(
                  run.affairs.filter((a) => a.slots.length > 0)
                )
              : []
          this.poolAffairsWithoutSlots = run.poolAffairsWithoutSlots ?? []
          this.runTechnicianHomes = run.technicianHomes ?? []
          this.tours = this.buildToursFromAffairs()
          this.rebuildMapTour()
          // Précharge toutes les interventions programmées de l'horizon (cache),
          // indépendamment du toggle : l'affichage reste piloté par le toggle.
          this.allScheduledInterventions = null
          this.invalidateScheduledRouteCache()
          this.fetchScheduledInterventions()
          this.loading = false
        },
        error: () => {
          this.error = "Erreur lors du chargement de l'optimisation"
          this.loading = false
        },
      })
    this.subscriptions.push(sub)
  }

  launchOptimization(): void {
    this.launchLoading = true
    this.launchError = null
    this.selectedTour = null
    this.highlightedAffairId = null
    this.rebuildMapTour()

    const sub = this.interventionsService.launchAffairOptimization().subscribe({
      next: (run) => {
        this.launchLoading = false
        this.run = run
        if (run.status === 'completed' && run.affairs?.length) {
          this.affairsWithSlots = this.sortSlotsByTravel(
            run.affairs.filter((a) => a.slots.length > 0)
          )
        } else {
          this.launchError = run.errorMessage ?? "Aucun résultat d'optimisation"
          this.affairsWithSlots = []
        }
        this.poolAffairsWithoutSlots = run.poolAffairsWithoutSlots ?? []
        this.runTechnicianHomes = run.technicianHomes ?? []
        this.tours = this.buildToursFromAffairs()
        this.rebuildMapTour()
        this.allScheduledInterventions = null
        this.invalidateScheduledRouteCache()
        this.fetchScheduledInterventions()
      },
      error: () => {
        this.launchLoading = false
        this.launchError = "Erreur lors de l'optimisation"
      },
    })
    this.subscriptions.push(sub)
  }

  // --- Selection ---

  selectTour(tour: OptimizationTour): void {
    if (this.selectedTour?.tourKey === tour.tourKey) {
      this.selectedTour = null
      this.highlightedAffairId = null
    } else {
      this.selectedTour = tour
      this.highlightedAffairId = null
      // Exclusivité : une tournée d'optimisation et une tournée d'interventions
      // programmées ne peuvent pas être affichées en même temps.
      this.clearScheduledRoute()
    }
    this.rebuildMapTour()
    this.lazyLoadGeometryIfNeeded()
  }

  highlightAffair(
    affairId: number,
    options: { scrollIntoView?: boolean } = {}
  ): void {
    if (this.highlightedAffairId === affairId) {
      this.highlightedAffairId = null
    } else {
      this.highlightedAffairId = affairId
    }
    this.rebuildMapTour()

    // Si demandé (typiquement clic depuis la timeline), faire défiler le
    // panneau droit jusqu'à la carte de l'affaire concernée.
    // On utilise un délai court pour laisser Angular rendre le bloc
    // des créneaux alternatifs (alt-slots) AVANT de calculer la position,
    // puis on positionne la carte en haut du scrollable avec un peu de marge.
    if (
      options.scrollIntoView &&
      this.highlightedAffairId === affairId &&
      typeof document !== 'undefined'
    ) {
      setTimeout(() => {
        const el = document.querySelector<HTMLElement>(
          `.tl-row[data-affair-id="${affairId}"]`
        )
        if (!el) return

        // Remonter jusqu'au premier ancêtre scrollable (right-column)
        let scrollParent: HTMLElement | null = el.parentElement
        while (scrollParent) {
          const { overflowY } = getComputedStyle(scrollParent)
          if (overflowY === 'auto' || overflowY === 'scroll') break
          scrollParent = scrollParent.parentElement
        }

        if (scrollParent) {
          const offset = el.offsetTop - scrollParent.offsetTop - 12
          scrollParent.scrollTo({ top: offset, behavior: 'smooth' })
        } else {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 50)
    }
  }

  /** ID de l'affaire dont l'adresse vient d'être copiée (feedback temporaire). */
  copiedAddressAffairId: number | null = null
  /** Affaire sans créneau actuellement sélectionnée (centrage carte). */
  selectedNoSlotAffairId: number | null = null

  @ViewChild(TablotMapComponent) private tablotMap?: TablotMapComponent

  /**
   * Copie « CP Ville » d'une affaire dans le presse-papiers. Affiche un
   * feedback visuel (icône check) pendant ~1.5s.
   */
  copyAffairAddress(
    affair: {
      affairId: number
      postalCode: string | null
      locality: string | null
    },
    event: Event
  ): void {
    event.stopPropagation()
    event.preventDefault()
    const parts = [affair.postalCode, affair.locality]
      .filter((p): p is string => !!p && p.trim().length > 0)
      .map((p) => p.trim())
    if (parts.length === 0) return
    const text = parts.join(' ')
    const done = () => {
      this.copiedAddressAffairId = affair.affairId
      setTimeout(() => {
        if (this.copiedAddressAffairId === affair.affairId) {
          this.copiedAddressAffairId = null
        }
      }, 1500)
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(done)
        .catch(() => this.fallbackCopy(text, done))
    } else {
      this.fallbackCopy(text, done)
    }
  }

  /** Fallback de copie via un textarea temporaire (anciens navigateurs / contextes non sécurisés). */
  private fallbackCopy(text: string, onDone: () => void): void {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      onDone()
    } catch {
      // silent
    }
  }

  onPoolMarkerClicked(affairId: number): void {
    // Trouver une tournée contenant cette affaire et la sélectionner
    const tour = this.tours.find((t) => t.poolAffairIds.includes(affairId))
    if (tour) {
      this.selectedTour = tour
      this.highlightedAffairId = affairId
      // Exclusivité avec la tournée d'interventions programmées.
      this.clearScheduledRoute()
      this.rebuildMapTour()
      this.lazyLoadGeometryIfNeeded()
    }
  }

  /**
   * Clic sur une intervention programmée → trace la tournée du jour de son
   * technicien (domicile → interventions du jour → domicile). Lazy : l'itinéraire
   * n'est calculé qu'au clic, puis mis en cache par "technicien + jour".
   */
  onScheduledInterventionClicked(interventionId: number): void {
    const marker = this.scheduledInterventions.find(
      (m) => m.id === interventionId
    )
    if (!marker || marker.technicianId == null || !marker.date) return

    const dayKey = marker.date.substring(0, 10)
    const routeKey = `${marker.technicianId}_${dayKey}`

    // Re-clic sur la même tournée → on la masque.
    if (this.scheduledRouteKey === routeKey) {
      this.clearScheduledRoute()
      return
    }

    // Exclusivité : on désélectionne la tournée d'optimisation éventuelle.
    if (this.selectedTour) {
      this.selectedTour = null
      this.highlightedAffairId = null
      this.rebuildMapTour()
    }

    // Déjà en cache → affichage immédiat, aucun appel réseau.
    const cached = this.scheduledRouteCache.get(routeKey)
    if (cached) {
      this.scheduledRoute = cached.route
      this.scheduledRouteRecap = cached.recap
      this.scheduledRouteOrder = cached.order
      this.scheduledRouteKey = routeKey
      this.scheduledRouteLoading = false
      return
    }

    // Interventions de ce technicien pour ce jour (depuis le cache déjà chargé).
    const dayInterventions = (this.allScheduledInterventions ?? [])
      .filter((it) => {
        const tech = it.data?.usersTechnicians?.[0]
        return (
          tech?.id === marker.technicianId &&
          (it.start ?? '').substring(0, 10) === dayKey
        )
      })
      .sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''))

    if (dayInterventions.length === 0) return

    // Récap + ordre des points affichés immédiatement ; le tracé suit (async).
    const recap = this.buildScheduledRouteRecap(dayInterventions, dayKey)
    const order = this.buildScheduledRouteOrder(recap)
    this.scheduledRouteRecap = recap
    this.scheduledRouteOrder = order
    this.scheduledRouteKey = routeKey
    this.scheduledRoute = null

    const stops = dayInterventions.map((it, index) => ({
      id: it.data?.id ?? `event-${index}`,
      title: it.title ?? 'Intervention',
      start: it.start,
      data: {
        workSiteLatitude: it.data?.workSiteLatitude,
        workSiteLongitude: it.data?.workSiteLongitude,
      },
    }))

    const color =
      dayInterventions[0]?.data?.usersTechnicians?.[0]?.color || '#8b5cf6'

    // Matching du technicien par EMAIL → id Planify (les id ERP varient selon
    // l'erpSource). Le back ajoute alors le domicile (départ + retour) via
    // User::find(userId). Sans match, on passe 0 → pas de domicile (route entre
    // les points seulement), plutôt qu'un id ERP qui pointerait un mauvais user.
    const email = marker.technicianEmail?.trim().toLowerCase() ?? ''
    const routeUserId = (email ? this.technicianIdByEmail.get(email) : 0) ?? 0

    this.scheduledRouteLoading = true
    const sub = this.interventionsService
      .getRoute(routeUserId, stops)
      .subscribe({
        next: (res) => {
          this.scheduledRouteLoading = false
          // L'utilisateur a pu changer de tournée entre-temps.
          if (this.scheduledRouteKey !== routeKey) return

          if (res?.summary) {
            recap.summary = this.formatRouteSummary(res.summary)
          }

          // Horaires domicile : uniquement si le domicile a été ajouté par le
          // back (routeUserId matché). legs_min[0] = trajet domicile→1er point,
          // dernier leg = trajet dernier point→domicile.
          // Les jours d'hôtel configurés, le back n'ajoute pas le domicile
          // (has_start_point / has_end_point = false) : pas de trajet affiché.
          if (routeUserId) {
            this.applyHomeTimes(
              recap,
              dayInterventions,
              res?.legs_min,
              res?.has_start_point !== false,
              res?.has_end_point !== false
            )
          }

          const coords = res?.geometry?.coordinates
          if (!coords?.length) return // pas de tracé, mais on garde le récap

          const route: ScheduledRoute = { coordinates: coords, color }
          this.scheduledRoute = route
          this.scheduledRouteCache.set(routeKey, { route, recap, order })
        },
        error: () => {
          this.scheduledRouteLoading = false
        },
      })
    this.subscriptions.push(sub)
  }

  /** Construit le récap (ordre + horaires) de la tournée du jour. */
  private buildScheduledRouteRecap(
    dayInterventions: InterventionCalendarModel[],
    dayKey: string
  ): ScheduledRouteRecap {
    const tech = dayInterventions[0]?.data?.usersTechnicians?.[0]
    const technicianName = tech
      ? `${tech.firstname} ${tech.lastname}`.trim()
      : 'Technicien'

    const stops: ScheduledRouteStop[] = dayInterventions.map((it, index) => {
      const data = it.data
      const start = this.formatLocalTime(it.start)
      const end = this.formatLocalTime(it.end)
      const time = end && end !== start ? `${start} - ${end}` : start
      const postal = data?.workSitePostalCode ?? ''
      const locality = data?.workSiteLocality ?? ''
      return {
        order: index + 1,
        time,
        title: it.title ?? 'Intervention',
        location: `${postal} ${locality}`.trim(),
        interventionId: data?.id ?? null,
        erpSource: (data as { erpSource?: string })?.erpSource ?? null,
      }
    })

    return {
      technicianName,
      dateLabel: formatDate(dayKey),
      stops,
      summary: null,
      homeDeparture: null,
      homeReturn: null,
    }
  }

  /** id intervention → n° d'ordre, pour numéroter les marqueurs sur la carte. */
  private buildScheduledRouteOrder(
    recap: ScheduledRouteRecap
  ): Record<number, number> {
    const order: Record<number, number> = {}
    for (const stop of recap.stops) {
      if (stop.interventionId != null) order[stop.interventionId] = stop.order
    }
    return order
  }

  /** "42 km · 1h20" à partir du résumé renvoyé par le back. */
  private formatRouteSummary(summary: {
    distance_km?: number
    duration_min?: number
  }): string | null {
    const parts: string[] = []
    if (summary.distance_km != null) {
      parts.push(formatDistance(summary.distance_km))
    }
    if (summary.duration_min != null) {
      parts.push(formatDuration(summary.duration_min))
    }
    return parts.length ? parts.join(' · ') : null
  }

  /** Heure locale "HH:MM" (cohérent avec les tooltips des interventions). */
  private formatLocalTime(value?: string | null): string {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  /** Heure locale "HH:MM" après décalage de `deltaMin` minutes (peut être négatif). */
  private shiftLocalTime(value?: string | null, deltaMin = 0): string {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    d.setMinutes(d.getMinutes() + Math.round(deltaMin))
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  /**
   * Renseigne les heures de départ/retour au domicile du récap à partir des
   * durées de trajet par leg renvoyées par le back (legs_min).
   * `hasStartPoint` / `hasEndPoint` indiquent si le back a inclus le point de
   * départ / d'arrivée dans la route (false les jours d'hôtel configurés :
   * aucun trajet domicile à afficher pour le leg concerné).
   */
  private applyHomeTimes(
    recap: ScheduledRouteRecap,
    dayInterventions: InterventionCalendarModel[],
    legsMin?: (number | null)[] | null,
    hasStartPoint = true,
    hasEndPoint = true
  ): void {
    if (!Array.isArray(legsMin) || legsMin.length === 0) return

    const first = dayInterventions[0]
    const last = dayInterventions[dayInterventions.length - 1]
    const fromHomeMin = hasStartPoint ? legsMin[0] : null
    const toHomeMin = hasEndPoint ? legsMin[legsMin.length - 1] : null

    if (first?.start && fromHomeMin != null) {
      recap.homeDeparture = this.shiftLocalTime(first.start, -fromHomeMin)
    }
    if (last?.end && toHomeMin != null) {
      recap.homeReturn = this.shiftLocalTime(last.end, toHomeMin)
    }
  }

  /** Fermeture du récap (bouton ×) → masque l'itinéraire. */
  onScheduledRouteRecapClosed(): void {
    this.clearScheduledRoute()
  }

  private clearScheduledRoute(): void {
    this.scheduledRoute = null
    this.scheduledRouteRecap = null
    this.scheduledRouteOrder = null
    this.scheduledRouteKey = null
    this.scheduledRouteLoading = false
  }

  /** Vide le cache des itinéraires (données rechargées → cache obsolète). */
  private invalidateScheduledRouteCache(): void {
    this.scheduledRouteCache.clear()
    this.clearScheduledRoute()
  }

  onGroupFilterChange(): void {
    this.rebuildMapTour()
  }

  onHiddenGroupsChange(hidden: Set<string>): void {
    this.hiddenMapGroups = hidden
    // Si le groupe actuellement sélectionné dans le dropdown vient d'être masqué,
    // on le réinitialise pour éviter un filtre sur un groupe invisible.
    if (this.selectedGroupingName && hidden.has(this.selectedGroupingName)) {
      this.selectedGroupingName = ''
    }
    this.rebuildMapTour()
  }

  clearSelectionAndSearch(): void {
    this.selectedTour = null
    this.highlightedAffairId = null
    this.searchQuery = ''
    this.selectedGroupingName = ''
    this.poolSearchQuery = ''
    this.erosOnly = false
    this.clearScheduledSearch()
    this.rebuildMapTour()
  }

  // --- Map tour building ---

  private sortSlotsByTravel(
    affairs: AffairOptimizationResult[]
  ): AffairOptimizationResult[] {
    const sortedAffairs = affairs.map((a) => ({
      ...a,
      slots: [...a.slots].sort((x, y) => this.compareSlots(x, y)),
    }))

    sortedAffairs.sort((a, b) => {
      const aBest = a.slots[0] ?? null
      const bBest = b.slots[0] ?? null

      if (!aBest && !bBest) return 0
      if (!aBest) return 1
      if (!bBest) return -1

      return this.compareSlots(aBest, bBest)
    })

    return sortedAffairs.map((a, index) => ({
      ...a,
      efficiencyRank: index + 1,
    }))
  }

  private compareSlots(
    a: AffairOptimizationSlotData,
    b: AffairOptimizationSlotData
  ): number {
    const stepsDiff = this.getSlotStopsCount(b) - this.getSlotStopsCount(a)
    if (stepsDiff !== 0) return stepsDiff

    const durationDiff = this.getSlotDurationMin(a) - this.getSlotDurationMin(b)
    if (durationDiff !== 0) return durationDiff

    return a.travelDurationMin - b.travelDurationMin
  }

  private getSlotStopsCount(slot: AffairOptimizationSlotData): number {
    if (slot.tourStopsCount != null) return slot.tourStopsCount

    const steps = slot.tourData?.steps
    if (!Array.isArray(steps)) return 0

    return steps.filter((s: any) => s.type !== 'start' && s.type !== 'end')
      .length
  }

  private getSlotDurationMin(slot: AffairOptimizationSlotData): number {
    if (slot.tourTotalDurationMin != null) return slot.tourTotalDurationMin

    return (
      (slot.travelDurationMin ?? 0) +
      (slot.serviceDurationMin ?? 0) +
      (slot.waitingMin ?? 0)
    )
  }

  getAffairGroupingName(affair: AffairOptimizationResult): string {
    return (affair.groupingName ?? affair.grouping_name ?? '').trim()
  }

  /**
   * Retourne les créneaux alternatifs d'une affaire (autres que la tournée sélectionnée).
   * Utile pour proposer une autre date si le client refuse la première.
   */
  getAffairAlternativeSlots(
    affair: AffairOptimizationResult
  ): AffairOptimizationSlotData[] {
    if (!this.selectedTour) return affair.slots
    return affair.slots.filter(
      (s) =>
        !(
          s.date === this.selectedTour!.date &&
          s.technicianId === this.selectedTour!.technicianId
        )
    )
  }

  /**
   * Sélectionne la tournée correspondant à un créneau alternatif et y met en avant l'affaire.
   */
  navigateToAlternativeSlot(
    slot: AffairOptimizationSlotData,
    affairId: number
  ): void {
    const key = `${slot.date}:${slot.technicianId}`
    const tour = this.tours.find((t) => t.tourKey === key)
    if (!tour) return
    this.selectedTour = tour
    this.highlightedAffairId = affairId
    this.rebuildMapTour()
    this.lazyLoadGeometryIfNeeded()
  }

  getGoogleMapsUrlForTour(): string | null {
    const tour = this.selectedTour
    if (!tour) return null

    const steps = this.selectedTourSteps
    if (steps.length === 0) return null

    // Trouver le domicile du technicien
    const home = this.runTechnicianHomes.find(
      (h) => h.technicianId === tour.technicianId
    )

    // Origine et destination = domicile (coordonnées) ou nom du technicien en fallback
    const homePoint = home
      ? `${home.latitude},${home.longitude}`
      : encodeURIComponent(tour.technicianName)

    // Waypoints = étapes intermédiaires (pool_affair + scheduled_intervention) dans l'ordre
    const waypointSteps = steps.filter(
      (s) => s.type === 'pool_affair' || s.type === 'scheduled_intervention'
    )
    if (waypointSteps.length === 0) return null

    const waypoints = waypointSteps
      .map((s) => {
        const parts = [s.postalCode, s.locality].filter(Boolean).join(' ')
        return parts ? encodeURIComponent(parts) : null
      })
      .filter(Boolean)
      .join('|')

    if (!waypoints) return null

    return `https://www.google.com/maps/dir/?api=1&origin=${homePoint}&destination=${homePoint}&waypoints=${waypoints}&travelmode=driving`
  }

  private getTablotFicheUrl(): string | null {
    const affair = this.highlightedAffair
    if (!affair || affair.source !== 'tablot') return null

    const proposals = this.buildTablotProposals(affair)
    if (proposals.length === 0) return null

    const baseUrl = affair.tablotFicheUrl?.trim()
    if (!baseUrl) return null

    const encodedProposals = encodeURIComponent(JSON.stringify(proposals))
    return `${baseUrl}?proposals=${encodedProposals}`
  }

  private buildTablotProposals(affair: AffairOptimizationResult): Array<{
    start_date: string
    end_date: string
    technician_email: string | null
  }> {
    return affair.slots
      .map((slot) => {
        if (!slot.arrivalTime) return null

        const start = new Date(slot.arrivalTime)
        if (Number.isNaN(start.getTime())) return null

        const durationMin =
          slot.serviceDurationMin && slot.serviceDurationMin > 0
            ? slot.serviceDurationMin
            : 120
        const end = new Date(start.getTime() + durationMin * 60 * 1000)

        return {
          start_date: this.formatTablotDateTime(start),
          end_date: this.formatTablotDateTime(end),
          technician_email: slot.technicianEmail ?? null,
        }
      })
      .filter(
        (
          proposal
        ): proposal is {
          start_date: string
          end_date: string
          technician_email: string | null
        } => !!proposal
      )
  }

  private buildTablotProposalFromStep(
    step: TourStepView,
    technicianEmail?: string | null
  ): {
    start_date: string
    end_date: string
    technician_email: string | null
  } | null {
    const startIso = step.plannedStart ?? step.arrival
    if (!startIso) return null

    const start = new Date(startIso)
    if (Number.isNaN(start.getTime())) return null

    let end: Date
    if (step.plannedEnd) {
      const plannedEnd = new Date(step.plannedEnd)
      if (Number.isNaN(plannedEnd.getTime())) return null
      end = plannedEnd
    } else {
      const durationMin = step.serviceMin > 0 ? step.serviceMin : 120
      end = new Date(start.getTime() + durationMin * 60 * 1000)
    }

    return {
      start_date: this.formatTablotDateTime(start),
      end_date: this.formatTablotDateTime(end),
      technician_email: technicianEmail ?? null,
    }
  }

  private buildOrderedTablotProposalsForStep(
    step: TourStepView,
    affair: AffairOptimizationResult
  ): Array<{
    start_date: string
    end_date: string
    technician_email: string | null
  }> {
    // Récupérer l'email du technicien depuis le slot correspondant à cette étape
    const tour = this.selectedTour
    const matchingSlot = tour
      ? affair.slots.find(
          (s) => s.date === tour.date && s.technicianId === tour.technicianId
        )
      : null
    const technicianEmail = matchingSlot?.technicianEmail ?? null

    const stepProposal = this.buildTablotProposalFromStep(step, technicianEmail)
    const allAffairProposals = this.buildTablotProposals(affair)

    const ordered = [
      ...(stepProposal ? [stepProposal] : []),
      ...allAffairProposals,
    ]

    const seen = new Set<string>()
    return ordered.filter((proposal) => {
      const key = `${proposal.start_date}|${proposal.end_date}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  private formatTablotDateTime(date: Date): string {
    const pad = (value: number) => value.toString().padStart(2, '0')
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  }

  private rebuildMapTour(): void {
    // Les compteurs par secteur dépendent du contenu courant du vivier.
    this.recomputeSectorCounts()

    // Rebuild pool markers : tous les points du vivier restent visibles ;
    // ceux hors du secteur actif sont marqués (inSector = false) pour être
    // atténués sur la carte, jamais masqués.
    const markersWithSlots: PoolMarker[] = this.affairsWithSlots
      .filter((a) => a.latitude && a.longitude)
      .map((a) => ({
        affairId: a.affairId,
        name: a.affairName,
        latitude: a.latitude,
        longitude: a.longitude,
        postalCode: a.postalCode,
        locality: a.locality,
        isSelected: a.affairId === this.highlightedAffairId,
        slotsCount: a.slots.length,
        bestTourDurationMin: a.bestTourDurationMin,
        hasSlots: true,
        groupName: this.getAffairGroupingName(a) || null,
        inSector: this.isInSelectedSector(a.latitude, a.longitude),
      }))

    const selectedAffairIds = new Set(markersWithSlots.map((m) => m.affairId))
    const markersWithoutSlots: PoolMarker[] = this.poolAffairsWithoutSlots
      .filter(
        (a) => a.latitude && a.longitude && !selectedAffairIds.has(a.affairId)
      )
      .map((a) => ({
        affairId: a.affairId,
        name: a.affairName,
        latitude: a.latitude,
        longitude: a.longitude,
        postalCode: a.postalCode,
        locality: a.locality,
        isSelected: false,
        slotsCount: 0,
        bestTourDurationMin: null,
        hasSlots: false,
        inSector: this.isInSelectedSector(a.latitude, a.longitude),
      }))

    this.mapPoolMarkers = [...markersWithSlots, ...markersWithoutSlots]

    if (this.runTechnicianHomes.length > 0) {
      // runTechnicianHomes n'a pas l'adresse : on la complète depuis le step start du premier slot disponible
      const homeAddressById = new Map<
        number,
        { postalCode: string | null; locality: string | null }
      >()
      for (const affair of this.affairsWithSlots) {
        for (const slot of affair.slots) {
          if (!slot.technicianId || homeAddressById.has(slot.technicianId))
            continue
          const startStep = (slot.tourData?.steps ?? []).find(
            (s: any) => s.type === 'start'
          )
          homeAddressById.set(slot.technicianId, {
            postalCode: startStep?.postal_code ?? null,
            locality: startStep?.locality ?? null,
          })
        }
      }

      this.mapTechnicianHomes = this.runTechnicianHomes.map((th) => {
        const parts = th.name.trim().split(/\s+/)
        const initials =
          parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : th.name.substring(0, 2).toUpperCase()
        const addr = homeAddressById.get(th.technicianId)
        return {
          id: th.technicianId,
          name: th.name,
          initials,
          latitude: th.latitude,
          longitude: th.longitude,
          postalCode: addr?.postalCode ?? null,
          locality: addr?.locality ?? null,
        }
      })
    } else {
      this.mapTechnicianHomes = this.extractTechnicianHomes()
    }

    const currentTour = this.selectedTour
    if (!currentTour?.tourData) {
      this.mapTours = []
      this.mapSelectedTour = null
      return
    }

    const mapTour: TablotTour = {
      user_id: currentTour.tourData.user_id ?? currentTour.technicianId ?? 0,
      technician_name:
        currentTour.tourData.technician_name ??
        currentTour.technicianName ??
        '',
      date: currentTour.tourData.date ?? currentTour.date,
      vehicle_id: 0,
      cost: 0,
      duration_min:
        currentTour.tourData.duration_min ?? currentTour.totalDurationMin ?? 0,
      service_min: 0,
      waiting_min: 0,
      distance_km:
        currentTour.tourData.distance_km ?? currentTour.totalDistanceKm ?? null,
      geometry: currentTour.geometry ?? null,
      steps: (currentTour.tourData.steps ?? []).map((s: any) => {
        const affairGroupName = s.affair_id
          ? this.affairsWithSlots.find((a) => a.affairId === s.affair_id)
            ? this.getAffairGroupingName(
                this.affairsWithSlots.find((a) => a.affairId === s.affair_id)!
              )
            : null
          : null
        return {
          type: s.type ?? 'job',
          arrival: s.arrival ?? null,
          duration_min: 0,
          service_min: 0,
          waiting_min: 0,
          distance_km: null,
          location: s.location ?? null,
          job_type: s.job_type ?? 'unknown',
          affair_id: s.affair_id ?? undefined,
          names: s.names ?? undefined,
          intervention_id: s.intervention_id ?? undefined,
          name: s.name ?? undefined,
          start_date: s.start_date ?? undefined,
          group_name: affairGroupName || null,
        } as TablotTourStep
      }),
    }

    this.mapTours = [mapTour]
    this.mapSelectedTour = mapTour
  }

  /**
   * Charge la géométrie de la tournée sélectionnée à la demande si elle est absente.
   *
   * @param onComplete callback exécuté une fois la géométrie chargée (succès ou échec)
   *                   ou immédiatement si aucun chargement n'est nécessaire. Utilisé
   *                   pour synchroniser l'extinction du spinner de recalcul avec la
   *                   disponibilité visuelle complète de la tournée mise à jour.
   */
  private lazyLoadGeometryIfNeeded(onComplete?: () => void): void {
    const tour = this.selectedTour
    if (!tour || tour.geometry || !tour.slotId || !tour.tourData) {
      onComplete?.()
      return
    }

    this.geometryLoading = true
    const slotId = tour.slotId
    const tourKey = tour.tourKey
    const sub = this.interventionsService.getSlotGeometry(slotId).subscribe({
      next: (result) => {
        this.geometryLoading = false
        if (!result.geometry) {
          onComplete?.()
          return
        }

        // Vérifier que la tournée sélectionnée n'a pas changé
        if (this.selectedTour?.tourKey !== tourKey) {
          onComplete?.()
          return
        }

        // Patcher le slot dans les données locales
        this.patchSlotGeometry(slotId, result.geometry)

        // Reconstruire les tournées pour récupérer la nouvelle géométrie
        const prevTourKey = this.selectedTour?.tourKey ?? null
        this.tours = this.buildToursFromAffairs()
        if (prevTourKey) {
          this.selectedTour =
            this.tours.find((t) => t.tourKey === prevTourKey) ?? null
        }

        this.rebuildMapTour()
        onComplete?.()
      },
      error: () => {
        this.geometryLoading = false
        onComplete?.()
      },
    })
    this.subscriptions.push(sub)
  }

  /**
   * Met à jour la géométrie d'un slot dans les données locales.
   */
  private patchSlotGeometry(slotId: number, geometry: string): void {
    const patchSlots = (slots: AffairOptimizationSlotData[]) =>
      slots.map((s) => (s.id === slotId ? { ...s, geometry } : s))

    this.affairsWithSlots = this.affairsWithSlots.map((a) => ({
      ...a,
      slots: patchSlots(a.slots),
    }))
  }

  private extractTechnicianHomes(): TechnicianHome[] {
    const seen = new Map<number, TechnicianHome>()

    for (const affair of this.affairsWithSlots) {
      for (const slot of affair.slots) {
        const techId = slot.technicianId
        const techName = slot.technicianName
        if (!techId || !techName || seen.has(techId)) continue

        // Find home location from tourData start step
        const startStep = (slot.tourData?.steps ?? []).find(
          (s: any) => s.type === 'start'
        )
        if (!startStep?.location || startStep.location.length < 2) continue

        const [lon, lat] = startStep.location
        if (!lat || !lon) continue

        const parts = techName.trim().split(/\s+/)
        const initials =
          parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : techName.substring(0, 2).toUpperCase()

        seen.set(techId, {
          id: techId,
          name: techName,
          initials,
          latitude: lat,
          longitude: lon,
          postalCode: startStep.postal_code ?? null,
          locality: startStep.locality ?? null,
        })
      }
    }

    return Array.from(seen.values())
  }

  /**
   * Construit la liste des tournées à partir des slots de toutes les affaires.
   * Chaque combinaison unique (date, technicianId) produit une tournée.
   * Le tourData étant partagé entre tous les slots d'une même tournée,
   * on le prend du premier slot rencontré.
   */
  private buildToursFromAffairs(): OptimizationTour[] {
    const tourMap = new Map<string, OptimizationTour>()

    for (const affair of this.affairsWithSlots) {
      for (const slot of affair.slots) {
        if (!slot.technicianId) continue
        const key = `${slot.date}:${slot.technicianId}`

        if (!tourMap.has(key)) {
          tourMap.set(key, {
            tourKey: key,
            date: slot.date,
            technicianId: slot.technicianId,
            technicianName: slot.technicianName ?? '',
            totalDurationMin: slot.tourTotalDurationMin ?? null,
            totalDistanceKm: slot.tourTotalDistanceKm ?? null,
            stopsCount: slot.tourStopsCount ?? null,
            poolAffairIds: [],
            tourData: slot.tourData,
            geometry: slot.geometry,
            slotId: slot.id,
          })
        }

        const tour = tourMap.get(key)!
        if (!tour.poolAffairIds.includes(affair.affairId)) {
          tour.poolAffairIds.push(affair.affairId)
        }
        // Prendre la géométrie du premier slot qui en a une
        if (!tour.geometry && slot.geometry) {
          tour.geometry = slot.geometry
          tour.slotId = slot.id
        }
        // Fusionner les steps de tous les slots pour ce tourKey
        if (slot.tourData?.steps) {
          const existingSteps: any[] = tour.tourData?.steps ?? []
          const existingAffairIds = new Set(
            existingSteps
              .filter((s: any) => s.affair_id != null)
              .map((s: any) => s.affair_id)
          )
          const newJobSteps = (slot.tourData.steps as any[]).filter(
            (s: any) =>
              s.affair_id != null &&
              s.type !== 'start' &&
              s.type !== 'end' &&
              !existingAffairIds.has(s.affair_id)
          )
          if (newJobSteps.length > 0) {
            // Insérer les nouvelles étapes avant le step 'end', puis trier l'ensemble
            // des étapes intermédiaires par heure d'arrivée pour garantir un affichage
            // chronologique même quand plusieurs slots alimentent la même tournée.
            const endIdx = existingSteps.findIndex((s: any) => s.type === 'end')
            const startStep = existingSteps.find((s: any) => s.type === 'start')
            const endStep = endIdx >= 0 ? existingSteps[endIdx] : undefined
            const middleSteps = existingSteps.filter(
              (s: any) => s.type !== 'start' && s.type !== 'end'
            )
            const allMiddle = [...middleSteps, ...newJobSteps].sort(
              (x: any, y: any) => {
                const ta = x.arrival ?? x.planned_start ?? ''
                const tb = y.arrival ?? y.planned_start ?? ''
                return ta < tb ? -1 : ta > tb ? 1 : 0
              }
            )
            const merged = [
              ...(startStep ? [startStep] : []),
              ...allMiddle,
              ...(endStep ? [endStep] : []),
            ]
            tour.tourData = { ...(tour.tourData ?? {}), steps: merged }
          }
          // Mettre à jour les métriques avec le slot le plus complet
          if ((slot.tourTotalDurationMin ?? 0) > (tour.totalDurationMin ?? 0)) {
            tour.totalDurationMin =
              slot.tourTotalDurationMin ?? tour.totalDurationMin
            tour.totalDistanceKm =
              slot.tourTotalDistanceKm ?? tour.totalDistanceKm
            tour.stopsCount = slot.tourStopsCount ?? tour.stopsCount
          }
        }
      }
    }

    // Trier : date la plus proche d'abord, puis par technicien (alphabétique)
    const sorted = Array.from(tourMap.values()).sort((a, b) => {
      const dateDiff = a.date.localeCompare(b.date)
      if (dateDiff !== 0) return dateDiff
      return a.technicianName.localeCompare(b.technicianName, 'fr')
    })

    // Injecter les placeholders pour les tournées en recalcul dont aucune affaire
    // n'est actuellement présente dans affairsWithSlots (ex: snooze d'une affaire
    // qui était seule sur sa tournée). Évite que le spinner ne s'affiche dans le vide.
    if (this.recalculatingTourSnapshots.size > 0) {
      for (const [key, snapshot] of this.recalculatingTourSnapshots) {
        if (!tourMap.has(key)) {
          sorted.push(snapshot)
        }
      }
      // Re-trier après ajout
      sorted.sort((a, b) => {
        const dateDiff = a.date.localeCompare(b.date)
        if (dateDiff !== 0) return dateDiff
        return a.technicianName.localeCompare(b.technicianName, 'fr')
      })
    }

    // Post-traitement : retirer des tourData.steps les étapes pool_affair dont
    // l'affaire n'existe plus dans affairsWithSlots (supprimée).
    // Retirer des tourData.steps les étapes pool_affair dont l'affaire n'est plus dans le vivier.
    // Les étapes scheduled_intervention, start et end sont toujours conservées.
    const knownAffairIds = new Set(this.affairsWithSlots.map((a) => a.affairId))
    return sorted.map((tour) => {
      if (!tour.tourData?.steps) return tour
      const originalSteps = tour.tourData.steps as any[]
      const cleanedSteps = originalSteps.filter((s: any) => {
        if (s.type === 'start' || s.type === 'end') return true
        if (s.job_type === 'scheduled_intervention') return true
        // étapes vivier : ne garder que si l'affaire est encore connue
        return s.affair_id == null || knownAffairIds.has(s.affair_id)
      })
      if (cleanedSteps.length === originalSteps.length) return tour
      return {
        ...tour,
        tourData: { ...tour.tourData, steps: cleanedSteps },
      }
    })
  }
}
